import { KText } from '@/ui-library/components';
import { Box, Flex } from '@chakra-ui/react';
import { useMemo } from 'react';
import { Button } from '@kula-ai/components';
import { Edit05, Download01 } from '@kula-ai/icons-core';

interface EmptyTokensWarningProps {
  nonFilledValidTokens: string[];
  invalidTokens: string[];
  detailsFormConfig: any[];
  onGoToDetails?: () => void;
  onDownload: () => void;
  ReplacementUploader: React.ComponentType<{ isLoading: boolean }>;
  isUploadLoading: boolean;
}

export const EmptyTokensWarning = ({
  nonFilledValidTokens,
  invalidTokens,
  detailsFormConfig,
  onGoToDetails,
  onDownload,
  ReplacementUploader,
  isUploadLoading,
}: EmptyTokensWarningProps) => {
  // Split missing fields by preapproval_enabled so the two groups are labelled separately.
  const { reapprovalLabels, nonReapprovalLabels } = useMemo(() => {
    const tokenSet = new Set([...nonFilledValidTokens, ...invalidTokens]);
    // One token can map to several conditional FE fields, so dedupe by tokenName (CORE-4828).
    const seenTokens = new Set<string>();
    const matched = detailsFormConfig
      .flatMap((section) => section?.fields ?? [section])
      .filter((field) => field?.tokenName && tokenSet.has(field.tokenName))
      .filter((field) => {
        if (seenTokens.has(field.tokenName)) return false;
        seenTokens.add(field.tokenName);
        return true;
      });
    return {
      reapprovalLabels: matched
        .filter((field) => field?.preapproval_enabled)
        .map((field) => field.label),
      nonReapprovalLabels: matched
        .filter((field) => !field?.preapproval_enabled)
        .map((field) => field.label),
    };
  }, [nonFilledValidTokens, invalidTokens, detailsFormConfig]);

  const hasReapproval = reapprovalLabels.length > 0;

  const renderFieldGroup = (title: string, labels: string[]) => (
    <Box mb="16px">
      <KText size="sm-semi-bold" color="secondary-700" mb="8px">
        {title}
      </KText>
      <Flex direction="column" gap="4px">
        {labels.map((label, i) => (
          <KText key={i} size="sm-regular" color="secondary-600">
            {label}
          </KText>
        ))}
      </Flex>
    </Box>
  );

  return (
    <Box p="24px" mt="12px">
      <KText size="md-semi-bold" color="secondary-900" mb="4px">
        Empty tokens detected in the offer letter template
      </KText>
      <KText size="sm-regular" color="secondary-600" mb="16px">
        Fill the missing information, switch offer letter templates or upload a
        new template to proceed
      </KText>

      {hasReapproval ? (
        <>
          {renderFieldGroup(
            'Required fields (re-approval required)',
            reapprovalLabels
          )}
          {nonReapprovalLabels.length > 0 &&
            renderFieldGroup(
              'Required fields (no re-approval required)',
              nonReapprovalLabels
            )}
        </>
      ) : (
        nonReapprovalLabels.length > 0 &&
        renderFieldGroup('Required fields', nonReapprovalLabels)
      )}

      <Flex gap="8px" mt="8px">
        {onGoToDetails && (
          <Button
            variant="secondary"
            leftElement={<Edit05 size={18} />}
            onClick={onGoToDetails}
          >
            Edit offer
          </Button>
        )}
        <Button
          variant="tertiary"
          onClick={onDownload}
          leftElement={<Download01 size={18} />}
        >
          Download
        </Button>
        <ReplacementUploader isLoading={isUploadLoading} />
      </Flex>
    </Box>
  );
};
